import { useEffect, useId, useState } from 'react';
import {
  HAYVEN_MARK_BACK_LEAF_PATH,
  HAYVEN_MARK_FRONT_LEAF_PATH,
  HAYVEN_MARK_LEAF_TILT,
  HAYVEN_MARK_LETTER_PATH,
  HAYVEN_MARK_VIEW_BOX,
} from '@repo/brand/hayven-mark-geometry';
import { logoColors } from '@repo/brand/palette';
import { usePrefersReducedMotion } from '@/hooks/use-prefers-reduced-motion';
import { cn } from '@/lib/utils';

const { degrees, pivotX, pivotY } = HAYVEN_MARK_LEAF_TILT;
// The back-leaf path is already drawn at the logo tilt: no rotation is the logo pose, and undoing
// the tilt lays the back leaf exactly behind the front leaf (the mark reads as one navy leaf).
const LEAF_LOGO_POSE = `0 ${pivotX} ${pivotY}`;
const LEAF_FOLDED_POSE = `${-degrees} ${pivotX} ${pivotY}`;

const SWING_MS = 1800;
const [VIEW_BOX_WIDTH, VIEW_BOX_HEIGHT] = HAYVEN_MARK_VIEW_BOX.split(' ').slice(2).map(Number) as [number, number];

/**
 * Boot shows a chain of loaders (static `index.html` splash → Clerk → workspace → org → route),
 * each a fresh mount. Without this, every hand-off would re-hide the mark for the reveal delay and
 * restart the swing, so the logo would blink and jump. A loader mounted while another is on screen
 * (or just left it) skips the reveal, and every swing is phase-locked to one shared clock that
 * starts at the first loader, so the first swing leaves the splash's resting logo pose.
 */
let mountedLoaders = 0;
let lastLoaderUnmountAt = Number.NEGATIVE_INFINITY;
let swingClockStartedAt: number | null = null;
const HAND_OFF_WINDOW_MS = 150;

/** The static mark `index.html` paints before React runs (`vite-plugin-boot-splash.ts`), faded in. */
function isBootSplashShowing(): boolean {
  const mark = document.getElementById('boot-splash')?.querySelector('svg');
  return mark ? getComputedStyle(mark).opacity !== '0' : false;
}

/** `animate-loader-reveal` for a fresh loading state; nothing when taking over from another loader. */
export function useLoaderRevealClass(): string {
  const [revealClass] = useState(() =>
    mountedLoaders > 0 || performance.now() - lastLoaderUnmountAt < HAND_OFF_WINDOW_MS || isBootSplashShowing()
      ? ''
      : 'animate-loader-reveal'
  );
  useEffect(() => {
    mountedLoaders += 1;
    return () => {
      mountedLoaders -= 1;
      lastLoaderUnmountAt = performance.now();
    };
  }, []);
  return revealClass;
}

/**
 * Swings the orange back leaf from the logo pose in behind the front leaf and back out. The same
 * animation drives the clip shape, so the navy overlap face reshapes live and every frame is a
 * true Hayven mark. Rests (and loops) on the logo pose.
 *
 * SMIL rather than CSS: CSS transforms on `clipPath` children aren't reliably re-rendered by
 * every engine, while SMIL `animateTransform` is.
 */
function LeafSwing({ begin }: { begin: string }) {
  return (
    <animateTransform
      attributeName="transform"
      type="rotate"
      begin={begin}
      dur={`${SWING_MS}ms`}
      repeatCount="indefinite"
      calcMode="spline"
      keyTimes="0;0.3;0.58;1"
      keySplines="0 0 1 1;0.65 0 0.35 1;0.65 0 0.35 1"
      values={`${LEAF_LOGO_POSE};${LEAF_LOGO_POSE};${LEAF_FOLDED_POSE};${LEAF_LOGO_POSE}`}
    />
  );
}

interface HayvenMarkLoaderProps {
  /** Pixel height of the mark (width follows the mark's ~0.97 aspect). Defaults to 44. */
  size?: number;
  className?: string;
}

/**
 * Branded loading mark: the Hayven logo with its back leaf folding and unfolding. Use for
 * page/section loads (app boot, route data, drawer bodies). Keep a plain spinner inside buttons,
 * inputs, and next to the logo lockup, where a second mark would be noise.
 *
 * Fades in after 200ms so fast loads never flash the logo. With reduced motion the mark is static.
 * Decorative only: the caller owns the `role="status"` / "Loading" text.
 */
export function HayvenMarkLoader({ size = 44, className }: HayvenMarkLoaderProps) {
  const reduceMotion = usePrefersReducedMotion();
  const revealClass = useLoaderRevealClass();
  const clipId = `hayven-loader-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  // Computed once: changing `begin` later would restart the SMIL animation.
  const [begin] = useState(() => {
    swingClockStartedAt ??= performance.now();
    return `-${Math.round((performance.now() - swingClockStartedAt) % SWING_MS)}ms`;
  });
  const swing = reduceMotion ? null : <LeafSwing begin={begin} />;

  return (
    <svg
      width={Math.round((size * VIEW_BOX_WIDTH) / VIEW_BOX_HEIGHT)}
      height={size}
      viewBox={HAYVEN_MARK_VIEW_BOX}
      aria-hidden="true"
      focusable="false"
      className={cn('shrink-0', revealClass, className)}
    >
      <defs>
        <clipPath id={clipId}>
          <path d={HAYVEN_MARK_BACK_LEAF_PATH}>{swing}</path>
        </clipPath>
      </defs>
      <path d={HAYVEN_MARK_BACK_LEAF_PATH} fill={logoColors.orange}>
        {swing}
      </path>
      <path d={HAYVEN_MARK_FRONT_LEAF_PATH} fill={logoColors.teal} />
      <path d={HAYVEN_MARK_FRONT_LEAF_PATH} fill={logoColors.navy} clipPath={`url(#${clipId})`} />
      <path d={HAYVEN_MARK_LETTER_PATH} fill={logoColors.white} />
    </svg>
  );
}
